import { useEffect, useMemo, useState } from 'react';
import { Menu } from '@mui/material';
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import PropTypes from 'prop-types';

import Button from '../buttons/Button';
import Checkbox from '../inputs/Checkbox';
import SearchBar from '../filters/SearchBar';

const DesignationsFilter = ({ initialDesignationIds, designationOptions, onSelect, active }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [designationIds, setDesignationIds] = useState(initialDesignationIds);
  const [searchTerm, setSearchTerm] = useState('');

  const open = Boolean(anchorEl);

  useEffect(() => {
    setDesignationIds(initialDesignationIds);
  }, [initialDesignationIds]);

  const filteredDesignationOptions = useMemo(() => {
    return [...designationOptions].filter((designation) =>
      designation.label.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [designationOptions, searchTerm]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (designationId) => {
    setDesignationIds((prev) => {
      const alreadySelected = prev.find((id) => id === designationId);
      if (alreadySelected) {
        return prev.filter((id) => id !== designationId);
      }
      return [...prev, designationId];
    });
  };

  const handleReset = () => {
    onSelect([]);
    setDesignationIds([]);
    setSearchTerm('');
    handleClose();
  };

  const handleApply = () => {
    onSelect(designationIds);
    handleClose();
  };

  return (
    <>
      <div className="flex w-fit">
        <Button
          title="Designation"
          className={`w-[140px] justify-between rounded-lg ${active ? 'border-primary-200 !bg-primary-50' : ''}`}
          RightIcon={open ? IconChevronUp : IconChevronDown}
          onClick={handleClick}
        />

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          classes={{ paper: '!min-w-80 !rounded-lg mt-1', list: '!p-0 divide-y divide-gray-50' }}
        >
          <li className="px-4 py-2">
            <SearchBar
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              containerClassName="max-w-none"
            />
          </li>
          <li className="max-h-[292px] space-y-3 overflow-y-auto p-4">
            {filteredDesignationOptions.map((designation) => (
              <Checkbox
                key={designation.value}
                id={designation.value}
                name={designation.label}
                label={designation.label}
                value={designation.value}
                onChange={() => handleSelect(designation.value)}
                checked={!!designationIds.find((id) => id === designation.value)}
              />
            ))}
          </li>

          <li className="flex items-center justify-between p-4">
            <Button title="Reset" onClick={handleReset} />

            <Button title="Apply" color="primary" onClick={handleApply} />
          </li>
        </Menu>
      </div>
    </>
  );
};

DesignationsFilter.propTypes = {
  initialDesignationIds: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
  designationOptions: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string.isRequired, value: PropTypes.string.isRequired }),
  ).isRequired,
  onSelect: PropTypes.func.isRequired,
  active: PropTypes.bool,
};

DesignationsFilter.defaultProps = {
  initialDesignationIds: [],
  active: false,
};

export default DesignationsFilter;
